import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Copy, Check, Wand2, Mail, AudioLines } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Persistent per-device identifier for fair-use limits (not tied to identity).
function getDeviceId() {
  let id = localStorage.getItem('poettik_device_id');
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('poettik_device_id', id);
  }
  return id;
}

const PLATFORMS = [
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@creator/video/…' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/reel/…' },
];

const FreeTranscriber = () => {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('tiktok');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [meta, setMeta] = useState(null);
  const [needEmail, setNeedEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [maxedOut, setMaxedOut] = useState(false);
  const [copied, setCopied] = useState(false);

  // "Email me the script" panel state (separate from the rate-limit email gate).
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [scriptEmail, setScriptEmail] = useState('');
  const [scriptConsent, setScriptConsent] = useState(false);
  const [emailingScript, setEmailingScript] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const active = PLATFORMS.find((p) => p.key === platform);

  const run = async () => {
    if (!url.trim()) {
      toast.error('Paste a video link first.');
      return;
    }
    if (needEmail && !email.trim()) {
      toast.error('Enter your email to unlock 2 more free transcripts.');
      return;
    }

    setLoading(true);
    setTranscript('');
    setMeta(null);
    try {
      const payload = {
        url: url.trim(),
        platform,
        device_id: getDeviceId(),
      };
      if (needEmail && email.trim()) payload.email = email.trim();

      const res = await axios.post(`${API}/api/free-transcribe`, payload);
      const data = res.data;

      if (data.success) {
        setTranscript(data.transcript);
        setMeta(data.meta || null);
        setNeedEmail(false);
        setShowEmailForm(false);
        setEmailSent(false);
        toast.success('Transcript ready.');
        return;
      }

      // Soft (expected) denials come back as success:false with a reason.
      if (data.reason === 'need_email') {
        setNeedEmail(true);
        toast.message(data.message);
      } else if (data.reason === 'email_exhausted' || data.reason === 'global_cap') {
        setMaxedOut(true);
        toast.error(data.message);
      } else {
        toast.error(data.message || 'Something went wrong.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not fetch that transcript. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyTranscript = async () => {
    await navigator.clipboard.writeText(transcript);
    setCopied(true);
    toast.success('Copied to clipboard.');
    setTimeout(() => setCopied(false), 1800);
  };

  // Carry the transcript into sign-up so it pre-loads as viral inspiration.
  const goGenerate = () => {
    try {
      localStorage.setItem(
        'poettik_pending_inspiration',
        JSON.stringify({ transcript, url, platform })
      );
    } catch { /* ignore quota errors */ }
    navigate('/register');
  };

  // Voiceover needs an account (free quota + saved keys) — carry the
  // transcript into sign-up with a voiceover intent flag.
  const goVoiceover = () => {
    try {
      localStorage.setItem(
        'poettik_pending_inspiration',
        JSON.stringify({ transcript, url, platform, intent: 'voiceover' })
      );
    } catch { /* ignore quota errors */ }
    navigate('/register');
  };

  // Email the transcript to the visitor + capture them as a lead (consent required).
  const emailScript = async () => {
    if (!scriptEmail.trim()) {
      toast.error('Enter your email address.');
      return;
    }
    if (!scriptConsent) {
      toast.error('Please tick the box to agree to receive the email.');
      return;
    }
    setEmailingScript(true);
    try {
      const res = await axios.post(`${API}/api/free-transcribe-email`, {
        email: scriptEmail.trim(),
        transcript,
        url,
        platform,
        consent: scriptConsent,
      });
      if (res.data?.success) {
        setEmailSent(true);
        setShowEmailForm(false);
        toast.success(res.data.message || 'Sent! Check your inbox.');
      } else {
        toast.error(res.data?.message || 'Could not send just now — try again shortly.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the email. Please try again.');
    } finally {
      setEmailingScript(false);
    }
  };

  return (
    <div
      className="w-full max-w-2xl mx-auto rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_12px_32px_rgba(70,45,20,0.06)] text-left"
      data-testid="free-transcriber"
    >
      <div className="flex items-center gap-2 mb-4">
        <Wand2 className="w-4 h-4 text-primary" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Free viral video transcriber
        </span>
      </div>

      {/* Platform toggle */}
      <div className="inline-flex p-1 rounded-xl bg-background/70 border border-border mb-4" data-testid="platform-toggle">
        {PLATFORMS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => { setPlatform(p.key); setTranscript(''); setMeta(null); }}
            data-testid={`platform-${p.key}`}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              platform === p.key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* URL input + submit */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !loading && run()}
          placeholder={active.placeholder}
          className="flex-1"
          data-testid="transcriber-url-input"
          disabled={maxedOut}
        />
        <Button
          variant="premium"
          onClick={run}
          disabled={loading || maxedOut}
          className="sm:min-w-[150px]"
          data-testid="transcriber-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transcribe free'}
        </Button>
      </div>

      {/* Email gate (shown after the first free go is used) */}
      {needEmail && !maxedOut && (
        <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20" data-testid="email-gate">
          <p className="text-sm text-muted-foreground mb-3">
            You've used your free transcript. Pop your email in for <strong className="text-foreground">2 more, free</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !loading && run()}
                placeholder="you@email.com"
                className="pl-9"
                data-testid="transcriber-email-input"
              />
            </div>
            <Button variant="premium" onClick={run} disabled={loading} data-testid="transcriber-email-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock 2 more'}
            </Button>
          </div>
        </div>
      )}

      {/* Maxed-out state */}
      {maxedOut && (
        <div className="mt-4 p-4 rounded-xl bg-muted/40 border border-border text-center" data-testid="transcriber-maxed">
          <p className="text-sm text-muted-foreground mb-3">
            That's your free transcripts used up. Create a free account to keep going.
          </p>
          <Link to="/register">
            <Button variant="premium" size="sm" data-testid="transcriber-signup-cta">Create free account</Button>
          </Link>
        </div>
      )}

      {/* Result */}
      {transcript && (
        <div className="mt-4" data-testid="transcript-result">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Transcript</span>
            <button
              type="button"
              onClick={copyTranscript}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              data-testid="copy-transcript-btn"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-xl bg-background/70 border border-border p-4 text-sm leading-relaxed whitespace-pre-wrap">
            {transcript}
          </div>

          {meta && (meta.views != null || meta.handle) && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="transcript-meta">
              {meta.views != null && (
                <span className="font-semibold text-foreground">
                  {meta.views >= 1e9 ? `${(meta.views / 1e9).toFixed(1).replace(/\.0$/, '')}B`
                    : meta.views >= 1e6 ? `${(meta.views / 1e6).toFixed(1).replace(/\.0$/, '')}M`
                    : meta.views >= 1e3 ? `${(meta.views / 1e3).toFixed(1).replace(/\.0$/, '')}K`
                    : meta.views} views
                </span>
              )}
              {meta.views != null && meta.handle && ' · '}
              {meta.handle && <span>@{meta.handle}</span>}
            </p>
          )}

          {/* Actions: Email me · Generate script · Create voiceover */}
          <div
            className="mt-4 p-4 rounded-xl bg-[hsl(var(--inset))] border border-border"
            data-testid="transcript-actions"
          >
            <p className="text-sm font-semibold mb-1 text-center">What next?</p>
            <p className="text-xs text-muted-foreground mb-3 text-center">
              Email this transcript to yourself, or let Poettik turn it into a ready-to-film script and an
              AI <strong className="text-foreground">voiceover</strong> for your own product.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowEmailForm((v) => !v); setEmailSent(false); }}
                data-testid="transcript-email-toggle"
              >
                <Mail className="w-4 h-4 mr-1.5" />
                {emailSent ? 'Sent ✓' : 'Email me'}
              </Button>
              <Button
                variant="premium"
                size="sm"
                onClick={goGenerate}
                data-testid="upsell-generate-cta"
              >
                <Wand2 className="w-4 h-4 mr-1.5" />
                Generate script
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goVoiceover}
                data-testid="upsell-voiceover-cta"
              >
                <AudioLines className="w-4 h-4 mr-1.5" />
                Create voiceover
              </Button>
            </div>

            {/* Email capture form (consent required) */}
            {showEmailForm && !emailSent && (
              <div className="mt-3 pt-3 border-t border-primary/20" data-testid="transcript-email-form">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={scriptEmail}
                      onChange={(e) => setScriptEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !emailingScript && emailScript()}
                      placeholder="you@email.com"
                      className="pl-9"
                      data-testid="transcript-email-input"
                    />
                  </div>
                  <Button
                    variant="premium"
                    size="sm"
                    onClick={emailScript}
                    disabled={emailingScript}
                    data-testid="transcript-email-send"
                  >
                    {emailingScript ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send it'}
                  </Button>
                </div>
                <label className="mt-2.5 flex items-start gap-2 cursor-pointer select-none">
                  <Checkbox
                    checked={scriptConsent}
                    onCheckedChange={(v) => setScriptConsent(Boolean(v))}
                    className="mt-0.5"
                    data-testid="transcript-email-consent"
                  />
                  <span className="text-[11px] leading-snug text-muted-foreground">
                    I agree to receive occasional emails from Poettik. You can opt out any time — just
                    reply &ldquo;unsubscribe&rdquo;.
                  </span>
                </label>
              </div>
            )}

            {emailSent && (
              <p className="mt-3 text-xs text-center text-primary" data-testid="transcript-email-sent">
                Transcript sent — check your inbox (and spam, just in case).
              </p>
            )}
          </div>
        </div>
      )}

      <p className="mt-3 font-mono text-[10px] text-muted-foreground">
        Free · no account needed · turn any script into an AI voiceover inside Poettik
      </p>
    </div>
  );
};

export default FreeTranscriber;
